import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { getI18nLangFromCorrespondenceLanguage } from '../../../../../../../shared/helpers/localization/correspondenceLanguage.helper';
import { take, zip, switchMap, of, map, filter } from 'rxjs';
import { IPopupWrapper } from '../../../../../../../shared/components/popup/components/popup.component';
import { IMultiEmail } from './sendMultipleEmails.popup';
import { responseHandler } from '../../../../../../../shared/responseHandler/responseHandler';
import { snackbarService } from '../../../../../../../shared/components/snackbar/service/snackbar.service';
import {
  C_Email_Msg_Doctype,
  SendReminderOfInvoiceEmailMutationVariables,
  EmailAttachmentInput,
  Wa_PdfFileOfInvoiceInput,
  C_E_Invoice_Format,
  C_Correspondence_Language,
} from '../../../../../../../graphql/generatedModel';
import {
  IPopupMarkupProps,
  PopupMarkup,
} from '../../../../../../../shared/components/multiEmailPopup/popupMarkup.component';
import { notificationCenter } from '../../../../../../../shared/notificationCenter/notificationCenter';
import {
  openDebtsService,
  SendReminderOfInvoiceWithIds,
} from '../../../../../services/openDebts.service.ts';
import { openDebtsState } from '../../../../states/openDebts.state.ts';
import {
  IGetMultipleR10NotifierFilesRes,
  IGetMultipleR64NotifierFilesRes,
  getCustomersWithoutNotifierFile,
  isMultipleR64NotifierFiles,
} from '../../../../../../../shared/services/reports/reports.service.ts';
import {
  reportsService,
  DataForMultiEmailsRes,
  ExtractXmlFromPdfOfInvoicesRes,
  ListWAPdfHistoryReportRes,
  REPORTS_TASK_FAILED_ERROR,
  stillBuildingNoticeForReportsError,
} from '../../../../../../../shared/services/reports/reports.service';
import {
  getSubjectForEmailWithDocument,
  getTextForEmailWithDocument,
  getFilesDataToExtractXmlFromPdfOfInvoices,
  getFileNameForEmailWithDocument,
  getFileNameForInvoiceXml,
} from '../../../../../../../shared/services/reports/reports.helper';

export const MultiEmailContent: FC<IEmailContent> = ({ stream, params }) => {
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<IPopupMarkupProps['tableData']>([]);
  const attachmentsOfEachCustomer = useRef<IAttachmentsOfCustomer[]>([]);
  const { t } = useTranslation();
  const { getMultiNotifierReportFilesPub, getMultiNotifierReportFilesSub, customerInvoices } =
    params || {};
  const ID_SEPARATOR = ',';

  useEffect(() => {
    // `responseHandler` turns an error into a VALUE: `customErrorHandler` runs and then
    // `errorReturnType` is emitted on the success path. Closing the popup from the error
    // handler therefore does not stop the rest of this pipeline, so the sentinel has to be
    // dropped explicitly — otherwise the attachment-building steps below run against empty
    // report data (WA-3801, Sentry WAWIASSIST-247).
    let requestFailed = false;
    const reportsServiceSub = zip(
      reportsService.sub.getDataForMultiEmails(),
      getMultiNotifierReportFilesSub(),
    )
      .pipe(
        responseHandler<
          [DataForMultiEmailsRes, IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes]
        >({
          customErrorHandler: (err) => {
            requestFailed = true;
            setLoading(false);
            stream.emit('close');
            // A batch whose reports are merely still being built is not a failure and not
            // actionable per occurrence, so it gets its own notice and no Sentry report —
            // which is what auto-filed a Jira ticket for each one (WA-3969).
            const stillBuilding = stillBuildingNoticeForReportsError(
              err.message,
              'production.report_still_building',
            );
            if (stillBuilding) return stillBuilding;
            // A task that ended in an error state IS a report problem, not a generic app
            // error: say so instead of `errors.unknown`.
            if (err.message?.startsWith(REPORTS_TASK_FAILED_ERROR)) {
              return {
                message: 'production.request_error_message',
                reportToSentry: true,
              };
            }
          },
          errorReturnType: [[], []],
        }),
        filter(() => !requestFailed),
        switchMap(
          (
            v: [
              DataForMultiEmailsRes,
              IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes,
            ],
          ) => {
            // this function extracts XML files from invoices (if needed) and passes to next function
            const [emailData, reportData] = v;

            const maybeR64NotifierFiles: IGetMultipleR64NotifierFilesRes =
              isMultipleR64NotifierFiles(reportData) ? reportData : [];

            let invoicePdfsToExtractXml: Array<Wa_PdfFileOfInvoiceInput> = [];
            emailData.forEach(({ customerId, eInvoiceFormat }) => {
              if (eInvoiceFormat === C_E_Invoice_Format.EIF2_XML_XRECHNUNG_PROFILE) {
                const filesOfCustomer = maybeR64NotifierFiles.filter(
                  (customerFilesData) => customerFilesData.customerId === customerId,
                );

                filesOfCustomer.forEach((customerFilesData) => {
                  const filesToExtractFrom = getFilesDataToExtractXmlFromPdfOfInvoices(
                    customerFilesData.invoicesPdfs,
                  );
                  invoicePdfsToExtractXml = invoicePdfsToExtractXml.concat(filesToExtractFrom);
                });
              }
            });

            if (invoicePdfsToExtractXml.length > 0) {
              reportsService.pub.extractXmlFromPdfOfInvoices({
                invoices: invoicePdfsToExtractXml,
              });
              return reportsService.sub
                .extractXmlFromPdfOfInvoices()
                .pipe(
                  map(
                    (extractedXmlWithEInvoice) =>
                      [emailData, reportData, extractedXmlWithEInvoice] as [
                        DataForMultiEmailsRes,
                        IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes,
                        ExtractXmlFromPdfOfInvoicesRes,
                      ],
                  ),
                );
            } else {
              return of([emailData, reportData, []] as [
                DataForMultiEmailsRes,
                IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes,
                ExtractXmlFromPdfOfInvoicesRes,
              ]);
            }
          },
        ),
      )
      .subscribe(
        (
          v: [
            DataForMultiEmailsRes,
            IGetMultipleR10NotifierFilesRes | IGetMultipleR64NotifierFilesRes,
            ExtractXmlFromPdfOfInvoicesRes,
          ],
        ) => {
          // prepare attachments for each customer. if e-invoice XML is provided, ignore invoice's PDF files
          const [emailData = [], reportData, extractedXmlWithEInvoice] = v;

          // A customer whose report task ended in WRTS15_NO_DATA_FOR_REPORT has no
          // reminder file: `reportsService` drops the task instead of failing the batch
          // (WA-3865). Such a customer must not be e-mailed — the message would carry no
          // attachment — so they leave the recipient list and are named to the user.
          const skippedCustomerIds = new Set(
            getCustomersWithoutNotifierFile(
              emailData.map(({ customerId }) => customerId),
              reportData,
            ),
          );
          const recipients = emailData.filter(
            ({ customerId }) => !skippedCustomerIds.has(customerId),
          );
          if (skippedCustomerIds.size > 0) {
            if (recipients.length === 0) {
              setLoading(false);
              stream.emit('close');
              snackbarService.pub.show({ type: 'info', content: t('common.no_data') });
              return;
            }
            const customers = emailData
              .filter(({ customerId }) => skippedCustomerIds.has(customerId))
              .map(
                ({ internalOrFullName, customerNo, customerId }) =>
                  internalOrFullName || customerNo || customerId,
              )
              .join(', ');
            snackbarService.pub.show({
              type: 'info',
              content: t('debitor.remindersSkippedNoData', { customers }),
            });
          }

          const mergedData = recipients.map((item) => {
            const documentId: string =
              customerInvoices?.[item.customerId].invoiceIds.join(ID_SEPARATOR) || '';
            return { ...item, documentId };
          });
          setTableData(mergedData);

          if (reportData.length > 0) {
            const isR64 = isMultipleR64NotifierFiles(reportData);
            const maybeR10NotifierFiles: IGetMultipleR10NotifierFilesRes = isR64
              ? []
              : (reportData as IGetMultipleR10NotifierFilesRes);
            const maybeR64NotifierFiles: IGetMultipleR64NotifierFilesRes = isR64 ? reportData : [];

            if (maybeR10NotifierFiles.length > 0) {
              recipients.forEach(({ customerId, correspondenceLanguage }) => {
                const tCustomer = getFixedTForCustomer(correspondenceLanguage);
                const filesOfCustomer = maybeR10NotifierFiles.filter(
                  (filesOfCustomer) => filesOfCustomer.customerId === customerId,
                );
                const reminderAttachments: Array<EmailAttachmentInput> = filesOfCustomer.map(
                  (el) => ({
                    url: el.url ?? '',
                    fileName: `${tCustomer('debitor.reminder')}.pdf`,
                  }),
                );

                attachmentsOfEachCustomer.current.push({
                  customerId: customerId,
                  attachments: reminderAttachments,
                });
              });
            } else if (maybeR64NotifierFiles.length > 0) {
              recipients.forEach(({ customerId, correspondenceLanguage }) => {
                const tCustomer = getFixedTForCustomer(correspondenceLanguage);
                const filesOfCustomer = maybeR64NotifierFiles.filter(
                  (filesOfCustomer) => filesOfCustomer.customerId === customerId,
                );

                const reminderAttachments: Array<EmailAttachmentInput> = filesOfCustomer.map(
                  (el) => ({
                    url: el.notifierPdfUrl,
                    fileName: `${tCustomer('debitor.reminder')}.pdf`,
                  }),
                );

                const invoiceAttachments: Array<EmailAttachmentInput> = [];

                const pdfFilesOfCustomer = filesOfCustomer.reduce(
                  (acc: ListWAPdfHistoryReportRes['reports'], { invoicesPdfs }) => {
                    acc = acc.concat(invoicesPdfs);
                    return acc;
                  },
                  [],
                );
                pdfFilesOfCustomer.forEach((reportFile) => {
                  const eInvoiceXmlFileOfDocument = extractedXmlWithEInvoice.find(
                    (xmlFileData) => xmlFileData.invoiceId === reportFile.documentId,
                  );

                  if (eInvoiceXmlFileOfDocument) {
                    const existingXmlAttachment = invoiceAttachments.find(
                      ({ url }) => url === eInvoiceXmlFileOfDocument.xmlFile.url,
                    );
                    if (!existingXmlAttachment) {
                      invoiceAttachments.push({
                        url: eInvoiceXmlFileOfDocument.xmlFile.url,
                        fileName: getFileNameForInvoiceXml(reportFile.documentNo),
                      });
                    }
                  } else {
                    invoiceAttachments.push({
                      url: reportFile.url ?? '',
                      fileName: getFileNameForEmailWithDocument(
                        t,
                        C_Email_Msg_Doctype.EML_INVOICE,
                        reportFile.documentNo,
                        reportFile.reportId,
                        correspondenceLanguage ?? C_Correspondence_Language.CL2_GERMAN,
                      ),
                    });
                  }
                });

                attachmentsOfEachCustomer.current.push({
                  customerId: customerId,
                  attachments: reminderAttachments.concat(invoiceAttachments),
                });
              });
            }
          }

          setLoading(false);
        },
      );

    const invoicesIds = Object.values(customerInvoices || {}).map((item) => item.invoiceIds[0]);
    reportsService.pub.getDataForMultiEmails({
      emailsDocType: C_Email_Msg_Doctype.EML_INVOICE,
      documentsIds: invoicesIds,
    });
    getMultiNotifierReportFilesPub();

    return () => {
      reportsServiceSub.unsubscribe();
    };
  }, []);

  const handleSendMultiEmail = () => {
    notificationCenter.notify<SendReminderOfInvoiceWithIds[]>(
      openDebtsService.sub.sendMultipleRemindersOfInvoiceEmail().pipe(
        responseHandler<SendReminderOfInvoiceWithIds[]>({
          errorReturnType: [],
        }),
      ),
      {
        type: 'snackbar',
        handler(src, target) {
          src.pipe(take(1)).subscribe({
            next: (data) => {
              const resultData = data as SendReminderOfInvoiceWithIds[];
              snackbarService.pub.hide('snackbarLoading');
              let successCount = 0;
              let failureCount = 0;
              resultData.forEach((res) => {
                if (res?.invoiceIds?.length) {
                  openDebtsState.pub.updateReminderCount(res.invoiceIds);
                }
                if (res.sentMessages?.length) {
                  successCount += res.sentMessages.length;
                }
                if (res.unsentMessages?.length) {
                  failureCount += res.unsentMessages?.length;
                }
              });
              if (failureCount === 0 && successCount > 0) {
                snackbarService.pub.show({
                  type: 'success',
                  content: t('common.emails_sent_successfully', { successCount }),
                });
              } else if (failureCount > 0 && successCount > 0) {
                snackbarService.pub.show({
                  type: 'warning',
                  content: `${t('common.emails_sent_successfully', { successCount })}\n${t(
                    'common.emails_failed_to_send',
                    { failureCount },
                  )}`,
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: t('common.emails_failed_to_send', { failureCount }),
                });
              }
              target.next(data);
            },
            error: (error) => {
              console.error('Error occurred while sending emails:', error);
              snackbarService.pub.hide('snackbarLoading');
              snackbarService.pub.show({
                type: 'error',
                content: t('common.error_while_sending_emails'),
              });
              target.error(error);
            },
          });
        },
      },
    );
    const emailsParams: Array<SendReminderOfInvoiceEmailMutationVariables> = tableData?.map(
      ({ email, customerId, documentId, correspondenceLanguage }) => {
        const attachments = attachmentsOfEachCustomer.current.find(
          (item) => item.customerId === customerId,
        )?.attachments;
        // Subject and text in the customer's correspondence language (WA-3363).
        const language = correspondenceLanguage ?? C_Correspondence_Language.CL2_GERMAN;
        return {
          emailParams: {
            to: email ? email.replace(/\s/g, '').split(/,|;/) : [],
            subject: getSubjectForEmailWithDocument(
              t,
              C_Email_Msg_Doctype.EML_REMINDER_OF_INVOICE,
              undefined,
              undefined,
              language,
            ),
            body: getTextForEmailWithDocument(
              t,
              C_Email_Msg_Doctype.EML_REMINDER_OF_INVOICE,
              undefined,
              language,
            ),
            attachments: attachments,
          },
          invoiceIds: documentId.split(ID_SEPARATOR),
        };
      },
    );
    openDebtsService.pub.sendMultipleRemindersOfInvoiceEmail(emailsParams);

    stream.emit('close');
  };

  return (
    <PopupMarkup
      tableData={tableData}
      loading={loading}
      onCancel={() => stream.emit('close')}
      onConfirm={handleSendMultiEmail}
      defaultEmailText={getTextForEmailWithDocument(t, C_Email_Msg_Doctype.EML_REMINDER_OF_INVOICE)}
    />
  );
};

interface IEmailContent {
  stream: IPopupWrapper['stream'];
  params: IMultiEmail['params'];
}

interface IAttachmentsOfCustomer {
  customerId: string;
  attachments: Array<EmailAttachmentInput>;
}

/** Translator for customer-facing strings (attachment names) in the customer's language. */
const getFixedTForCustomer = (
  correspondenceLanguage: C_Correspondence_Language | null | undefined,
) =>
  i18next.getFixedT(
    getI18nLangFromCorrespondenceLanguage(
      correspondenceLanguage ?? C_Correspondence_Language.CL2_GERMAN,
    ),
  );
